import {
  faBarcode,
  faBuildingColumns,
  faCreditCard,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, CreditCardLogo, SelectRow, TextButton } from "kubra-ux-forge";
import { useEffect, useState } from "react";

import { ACTION } from "actions/paymentIntentActions";
import ApplePayImg from "assets/icons/applepay.svg";
import GooglePayImg from "assets/icons/googlepay.svg";
import PayPalImg from "assets/icons/paypal.svg";
import VenmoImg from "assets/icons/venmo.svg";
import noFundingSourceImage from "assets/no-funding-source-available.svg";
import FundingOption, {
  ApplePay,
  BankAccount,
  Card,
  getCardNetworkByShortCode,
  GooglePay,
  isPaymentMethodAllowed,
  PayPal,
  translateBankAccountType,
  Venmo,
} from "constants/FundingOption";
import { SELECT_PAYMENT_METHOD_MODAL_NAMESPACE } from "constants/i18n-namespaces";
import { useDateFormatter } from "hooks";
import useWallet from "hooks/useWallet";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBiller } from "selectors";
import { getPayableSession } from "selectors/payableSession";
import { getPaymentIntent } from "selectors/paymentIntent";
import { PaymentIntentPrepareRequest } from "types/Api/Payment/paymentIntentPrepareRequest";
import { WalletItemResponse } from "types/Api/Wallet/walletItemResponse";
import { DigitalPaymentData } from "types/digitalPaymentData";
import { loadApplePaySDKIfNeeded } from "utilities/applePaySDKLoader";
import { phoneFormat } from "utilities/helper";
import { MethodComponentsOptions } from "../../payable-dashboard/payable-dashboard";

export const MAXIMUM_WALLET_ITEMS = 3;

export interface ISelectPaymentMethodModalProps {
  flowCallback: (arg0: MethodComponentsOptions) => any;
  closeModalCallBack: () => void;
  showManagePaymentMethodsModal: () => void;
  selectedPaymentMethodId?: string;
  setIsCashView?: (isCashView: boolean) => void;
  reviewDigitalPayment: (paymentData: DigitalPaymentData) => void;
  verifyPaymentAmount: () => { isValid: boolean };
  onPrepareIntentError: (
    err: any,
    prepareRequest: PaymentIntentPrepareRequest
  ) => void;
}

export const SelectPaymentMethodModal = (
  props: ISelectPaymentMethodModalProps
) => {
  const {
    flowCallback,
    closeModalCallBack,
    showManagePaymentMethodsModal,
    selectedPaymentMethodId,
    setIsCashView,
  } = props;

  const { t } = useTranslation([SELECT_PAYMENT_METHOD_MODAL_NAMESPACE]);
  const [isAppleDevice, setIsAppleDevice] = useState(false);
  const dateFormatter = useDateFormatter();
  const paymentIntentState = useSelector(getPaymentIntent);
  const dispatch = useDispatch();
  const { getWalletItems } = useWallet(true);
  const paymentDate = dateFormatter.shortIsoFormat(paymentIntentState.paymentDate, "en-US");
  const todayDateString = dateFormatter.shortIsoFormat(new Date(), "en-US");

  // TODO: sometimes there is a noticable delay before the wallet item are displayed when this
  // modal is opened because that's exactly when the API call is made i.e when the hook is used first.
  // Consider making the API call when we land on payable dashboard to speed things up?
  const walletItems = getWalletItems();
  const biller = useSelector(getBiller);
  const payableSession = useSelector(getPayableSession);

  const handleWalletItemClick = (selectedWalletItem: WalletItemResponse) => {
    if (selectedWalletItem) {
      dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
      dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
      dispatch({ type: ACTION.ADD_WALLET, payload: selectedWalletItem });

      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.Wallet,
      });

      closeModalCallBack();
    }
  };

  const handleDigitalWalletClick = (digitalWallet: any) => {
   setIsCashView?.(false);
    dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
    dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
    dispatch({ type: ACTION.ADD_WALLET, payload: null });

    if (digitalWallet == FundingOption.GooglePay) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.GooglePay,
      });
    } else if (digitalWallet == FundingOption.ApplePay) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.ApplePay,
      });
    } else if (digitalWallet == FundingOption.PayPal) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.PayPal,
      });
    } else if (digitalWallet == FundingOption.Venmo) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.Venmo,
      });
    }

    closeModalCallBack();
  };

  const payableFsOptions = payableSession.fundingOptions;
  const digitalPaymentOptions = payableFsOptions.filter(option => option !== 'Card' && option !== 'BankAccount')

  // Detect if the user is on an Apple device and load Apple Pay SDK if needed
  useEffect(() => {
    const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
    setIsAppleDevice(isApple);
    
    // Load Apple Pay SDK if it's an available funding option
    loadApplePaySDKIfNeeded(payableFsOptions);
  }, [payableFsOptions]);

  const contactUs: any = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  });

  // No funding source available
  if (payableFsOptions.length === 0 && !payableSession?.payableType?.allowCashPayment) {
    return (
      <div
        data-testid="no-funding-source-available-div"
        className="payment-method-frame"
      >
        <img
          data-testid="no-funding-source-image"
          src={noFundingSourceImage}
          alt="no funding source available"
        ></img>
        <div className="no-funding-source-available">
          {t("no-funding-source-available", {
            biller: biller.billerName,
            phone: phoneFormat(contactUs.properties.phone),
          })}
        </div>
      </div>
    );
  }

  const hasAnyDigitalPaymentMethod =
    isPaymentMethodAllowed(ApplePay, payableFsOptions) ||
    isPaymentMethodAllowed(GooglePay, payableFsOptions) ||
    isPaymentMethodAllowed(PayPal, payableFsOptions) ||
    isPaymentMethodAllowed(Venmo, payableFsOptions);

  const getDigitalWalletDisableText = (digitalPaymentOptions: string[]) => {
    if (digitalPaymentOptions.length === 1) {
      return t('one-digital-payment-available', { paymenttype: digitalPaymentOptions[0] });
    } else if (digitalPaymentOptions.length > 1 && digitalPaymentOptions.length < 3) {
      return t('two-or-more-digital-payment-available', { paymenttype1: digitalPaymentOptions[0], paymenttype2: digitalPaymentOptions[1] });
    } else if (digitalPaymentOptions.length >= 3) {
      const allButLast = digitalPaymentOptions.slice(0, -1).join(', ');
      const last = digitalPaymentOptions.slice(-1);
      return t('two-or-more-digital-payment-available', { paymenttype1: allButLast, paymenttype2: last });
    }
    return '';
  };

  const bankCardCashNotAllowed = !isPaymentMethodAllowed(BankAccount, payableFsOptions) 
    && !isPaymentMethodAllowed(Card, payableFsOptions)
    && !payableSession?.payableType?.allowCashPayment;

  const paymentMethodGroupLabelId = "payment-method-options-group-label";
  const walletItemsGroupLabelId = "saved-payment-methods-group-label";
  const digitalMethodGroupLabelId = "digital-payment-methods-group-label";

  return (
    <div className="select-paymentmethod-container">
      <span className="sr-only" id={paymentMethodGroupLabelId}>
        {t("select-payment-method")}
      </span>
      <div
        className="method-buttons-container"
        role="group"
        aria-labelledby={paymentMethodGroupLabelId}
      >
        {isPaymentMethodAllowed(BankAccount, payableFsOptions) && (     
          <Button
            className="method-button"
            onClick={() => {
              setIsCashView?.(false);
              flowCallback("PayableBankAccountModal");
            }}
            variant="tonal"
            data-cy="button-tonal-bank"
          >
            <div className="payment-method-btn">
              <FontAwesomeIcon
                  icon={faBuildingColumns}
                  style={{ color: "#007FAD" }}
                />
              <span className="payment-method-display-name">{t("bank")}</span>
            </div>
          </Button>
        )}

        {isPaymentMethodAllowed(Card, payableFsOptions) && (
          <Button
            className="method-button"
            onClick={() => {
              setIsCashView?.(false);
              flowCallback("PayableCreditDebitModal");
            }}
            variant="tonal"
            data-cy="button-tonal-card"
          >
            <div className="payment-method-btn">
              <FontAwesomeIcon
                icon={faCreditCard}
                style={{ color: "#007FAD" }}
              />
              <span className="payment-method-display-name">{t("card")}</span>
            </div>
          </Button>
        )}

        {payableSession?.payableType?.allowCashPayment && (
          <Button
            className="method-button"
            onClick={() => {
              dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
              dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
              dispatch({ type: ACTION.ADD_WALLET, payload: null });
              dispatch({ type: ACTION.ADD_DIGITAL_WALLET, payload: null });
              dispatch({
                type: ACTION.ADD_PAYMENT_METHOD,
                payload: FundingOption.Cash,
              });
              setIsCashView?.(true);
              closeModalCallBack();
            }}
            variant="tonal"
            data-cy="button-tonal-cash"
          >
            <div className="payment-method-btn">
              <FontAwesomeIcon
                icon={faBarcode}
                style={{ color: "#007FAD" }}
              />
              <span className="payment-method-display-name">{t("cash")}</span>
            </div>
          </Button>
        )}
      </div>

      <span className="sr-only" id={walletItemsGroupLabelId}>
        {t("saved-payment-methods-group")}
      </span>
      <div
        className="wallet-items-container"
        role="group"
        aria-labelledby={walletItemsGroupLabelId}
      >
        {_.sortBy(
          walletItems,
          (item) => -new Date(item.enrolledDate).getTime()
        ).map((walletItem) => {
          const isSelected =
            selectedPaymentMethodId === walletItem.fundingSourceId;
          const extraStyle = isSelected
            ? {
              borderRadius: "8px",
              border: "3px solid var(--primary-primary-60, #279ACE)",
              background: "var(--primary-primary-95, #464748)",
            }
            : {};

          if (
            isPaymentMethodAllowed(BankAccount, payableFsOptions) &&
            walletItem.fundingOption === BankAccount
          ) {
            return (
              <SelectRow
                key={walletItem.fundingSourceId}
                icon={
                  <FontAwesomeIcon icon={faBuildingColumns}></FontAwesomeIcon>
                }
                label={`${translateBankAccountType(
                  walletItem.presentableMOPCode,
                  t
                )} ${walletItem.maskedNumber}`}
                /* @ts-ignore */
                className="wallet-item-row"
                variant="static"
                style={extraStyle}
                dataCy={`payment-method-row-${walletItem.presentableMOPCode} ${walletItem.maskedNumber}`}
                onClick={() => {
                  setIsCashView?.(false);
                  handleWalletItemClick(walletItem);
                }}
              />
            );
          } else if (
            isPaymentMethodAllowed(Card, payableFsOptions) &&
            walletItem.fundingOption === Card
          ) {
            return (
              <SelectRow
                key={walletItem.fundingSourceId}
                icon={
                  <CreditCardLogo
                    cardType={_.lowerCase(
                      getCardNetworkByShortCode(walletItem.presentableMOPCode)
                    )}
                  />
                }
                label={`${getCardNetworkByShortCode(
                  walletItem.presentableMOPCode
                )} ${walletItem.maskedNumber}`}
                /* @ts-ignore */
                className="wallet-item-row"
                variant="static"
                style={extraStyle}
                dataCy={`payment-method-row-${getCardNetworkByShortCode(
                  walletItem.presentableMOPCode
                )} ${walletItem.maskedNumber}`}
                onClick={() => {
                  setIsCashView && setIsCashView(false);
                  handleWalletItemClick(walletItem);
                }}
              />
            );
          }
          return null;
        })}
      </div>

      {walletItems.length > 0 ? (
        <div className="manage-payment-methods-container">
          <TextButton
            onClick={() => {
              closeModalCallBack();
              showManagePaymentMethodsModal();
            }}
          >
            {t("manage-payment-methods-button")}
          </TextButton>
        </div>
      ) : null}

      {hasAnyDigitalPaymentMethod ? (
        paymentDate === todayDateString ? (
          <>
            {!bankCardCashNotAllowed && (
              <div className="or-separator-container">
                <span className="or-separator-element"> {t("or")}</span>
              </div>
            )}
            <div className="digital-methods-container">
              {isPaymentMethodAllowed(ApplePay, payableFsOptions) ||
                isPaymentMethodAllowed(GooglePay, payableFsOptions) ||
                isPaymentMethodAllowed(PayPal, payableFsOptions) ||
                isPaymentMethodAllowed(Venmo, payableFsOptions) ? (
                <>
                <span className="sr-only" id={digitalMethodGroupLabelId}>
                  {t("digital-payment-methods-group")}
                </span>
                <div
                  className="digital-method-button-container"
                  role="group"
                  aria-labelledby={digitalMethodGroupLabelId}
                >
                  {isPaymentMethodAllowed(ApplePay, payableFsOptions) &&
                    isPaymentMethodAllowed(Card, payableFsOptions) && (
                      <Button
                        className="digital-method-button"
                        data-cy="button-apple-pay"
                        onClick={() => handleDigitalWalletClick(ApplePay)}
                        dataCy="button-apple-pay"
                      >
                        <img src={ApplePayImg} alt="ApplePay" />
                      </Button>
                    )}
                  {isPaymentMethodAllowed(GooglePay, payableFsOptions) &&
                    isPaymentMethodAllowed(Card, payableFsOptions) && (
                      <Button
                        className="digital-method-button"
                        data-cy="button-google-pay" 
                        onClick={() => handleDigitalWalletClick(GooglePay)}
                        dataCy="button-google-pay"
                      >
                        <img src={GooglePayImg} alt="GooglePay" />
                      </Button>
                  )}
                  {isPaymentMethodAllowed(PayPal, payableFsOptions) && (
                    <Button
                      className="digital-method-button"
                      data-cy="button-paypal"
                      onClick={() => handleDigitalWalletClick(PayPal)}
                      dataCy="button-paypal"
                    >
                      <img src={PayPalImg} alt="PayPal" />
                    </Button>
                  )}
                  {isPaymentMethodAllowed(Venmo, payableFsOptions) && (
                    <Button
                      className="digital-method-button"
                      data-cy="button-venmo"
                      onClick={() => handleDigitalWalletClick(Venmo)}
                      dataCy="button-venmo"
                    >
                      <img src={VenmoImg} alt="Venmo" />
                    </Button>
                  )}
                </div>
                </>) : null}
            </div>
          </>
        ) : (
          <div className="digital-wallets-disabled-message-container" >
            <div className="digital-wallets-disabled-message-inner-container" >
              <div className="digital-wallets-disabled-image">
                <img
                  src={require("assets/icons/hexagon_exclamation.png")}
                  alt="exclamation"
                />
              </div>
            </div>
            <div className="digital-wallets-disabled-message">
              {getDigitalWalletDisableText(digitalPaymentOptions)}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};
